import React, { useEffect, useState } from "react";
import { ExtendedUser } from "@/types/encryption";
import { BasicUserType } from "@/types/user";
// @ts-ignore - Prisma client types
import type {  User  } from '@prisma/client';
import { useToast } from "@/components/ui/use-toast";
import { follow, unfollow } from "@/actions/account";
import notify from "@/lib/notify";

interface PostUserInfoProps {
  id: string;
  userId: string;
  username: string;
  currentUser?: BasicUserType | null;
  onUserInfoChange: (targetUser: User | null, isFollowing: boolean) => void;
}

export function PostUserInfo({
  id,
  userId,
  username,
  currentUser,
  onUserInfoChange,
}: PostUserInfoProps) {
  const [targetUser, setTargetUser] = useState<User | null>(null);
  const [isFollowing, setIsFollowing] = useState(false);
  const { toast } = useToast();

  // Fetch user data once on mount or when userId changes
  useEffect(() => {
    let isMounted = true;

    const fetchUser = async () => {
      if (!userId) return;
      
      try {
        const response = await fetch(`${process?.env?.NEXT_PUBLIC_API_URL}/users/${userId}`);
        if (!response.ok) {
          if (response.status === 404) {
            console.log("User not found");
            return;
          }
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const userData = await response.json();
        if (isMounted) {
          setTargetUser(userData);
          onUserInfoChange(userData, isFollowing);
        }
      } catch (error) {
        console.error("Error fetching user:", error);
      }
    };

    fetchUser();

    return () => {
      isMounted = false;
    };
  }, [userId]); // Only re-run when userId changes

  // Check following status once on mount or when currentUser changes
  useEffect(() => {
    let isMounted = true;

    const checkFollowing = async () => {
      try {
        if (!currentUser?.id || !userId) return;
        
        // Temporarily disable following status check to prevent 404 errors and infinite loops
        // TODO: Implement proper following endpoint on the API
        // const response = await fetch(`${process?.env?.NEXT_PUBLIC_API_URL}/users/${userId}/following`);
        // if (response.ok && isMounted) {
        //   const followingStatus = await response.json();
        //   setIsFollowing(followingStatus);
        //   if (targetUser) {
        //     onUserInfoChange(targetUser, followingStatus);
        //   }
        // }
        
        // For now, just set following status to false to prevent infinite API calls
        setIsFollowing(false);
        if (targetUser && isMounted) {
          onUserInfoChange(targetUser, false);
        }
      } catch (error) {
        console.error('Error checking following status:', error);
      }
    };

    checkFollowing();

    return () => {
      isMounted = false;
    };
  }, [userId, currentUser?.id, targetUser]); // Only re-run when userId, currentUser, or targetUser changes

  const handleFollowToggle = async () => {
    if (!currentUser || !targetUser) {
      toast({
        title: "Error",
        description: "Please log in to follow users",
        variant: "destructive",
      });
      return;
    }

    try {
      // Convert BasicUserType to ExtendedUser
      const extendedCurrentUser: ExtendedUser = {
        ...currentUser,
        notifications: [],
        conversations: [],
        publicKey: null,
        privateKey: null,
        encryptionKey: null,
        encryptedData: null,
        posts: []
      };

      // Convert User to ExtendedUser
      const extendedTargetUser: ExtendedUser = {
        ...targetUser,
        id: String(targetUser.id),
        followers: targetUser?.followers?.map(String),
        following: targetUser?.following?.map(String),
        notifications: [],
        conversations: [],
        publicKey: null,
        privateKey: null,
        encryptionKey: null,
        encryptedData: null,
        posts: []
      };

      if (isFollowing) {
        await unfollow(currentUser.id, String(targetUser.id));
        setIsFollowing(false);
        onUserInfoChange(targetUser, false);
      } else {
        await follow(currentUser.id, String(targetUser.id));
        setIsFollowing(true);
        onUserInfoChange(targetUser, true);

        if (id !== currentUser.id) {
          notify({
            userId: id,
            type: "follow",
            message: `${currentUser.name} started following you`,
          });
        }
      }
    } catch (error) {
      console.error('Error toggling follow:', error);
      toast({
        title: "Error",
        description: "Failed to update follow status",
        variant: "destructive",
      });
    }
  };

  return {
    targetUser,
    isFollowing,
    handleFollowToggle,
  };
}